/**
 * Reusable form section for creating multiple treatment goals within an encounter form.
 * @agentic @benchmark/core
 */

// Components
import { Button, Stack, Textarea, Select, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { EncounterGoalCard } from "@/components/domain/encounter/shared/EncounterGoalCard";

// Constants
import { GOAL_CATEGORIES } from "@benchmark/core/constants";

// Types
import type { UseFormReturnType } from "@mantine/form";
import { useMemo, useCallback } from "react";

// Types
/**
 * Data structure for a single treatment goal in the goal creation form.
 * @agentic @benchmark/core
 */
export type GoalCreationData = {
  category: string;
  description: string;
  notes?: string;
};

/**
 * Form-shape contract required by {@link GoalCreationSection}. Any form
 * that owns the `goal-creation` field can be passed in without the
 * caller needing an `as unknown as` bridge.
 *
 * @agentic @benchmark/core
 */
export type GoalCreationCapableFormShape = {
  "goal-creation"?: GoalCreationData[];
};

/**
 * Props for GoalCreationSection component. Generic over the host form's
 * shape so each consumer can pass its `useForm<T>` directly without
 * casts.
 *
 * @agentic @benchmark/core
 */
export type GoalCreationSectionProps<T extends GoalCreationCapableFormShape> = {
  form: UseFormReturnType<T>;
  disabled?: boolean;
};

/**
 * Form section for adding multiple treatment goals with category and description.
 * @agentic @benchmark/core
 */
export const GoalCreationSection = <T extends GoalCreationCapableFormShape>({
  form,
  disabled = false,
}: GoalCreationSectionProps<T>) => {
  const goals = form.values["goal-creation"] || [];

  // Prepare category options
  const categoryOptions = useMemo(
    () =>
      GOAL_CATEGORIES.map((cat) => ({
        value: cat.code,
        label: cat.display,
      })),
    [],
  );

  const addGoal = useCallback(() => {
    const currentGoals = form.values["goal-creation"] || [];
    form.setFieldValue(
      "goal-creation" as never,
      [...currentGoals, { category: "", description: "" }] as never,
    );
  }, [form]);

  const removeGoal = useCallback(
    (index: number) => {
      const currentGoals = form.values["goal-creation"] || [];
      const updated = currentGoals.filter((_, i) => i !== index);
      form.setFieldValue(
        "goal-creation" as never,
        (updated.length > 0 ? updated : undefined) as never,
      );
    },
    [form],
  );

  const updateGoal = useCallback(
    (index: number, field: keyof GoalCreationData, value: string) => {
      const currentGoals = form.values["goal-creation"] || [];
      const updated = [...currentGoals];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      form.setFieldValue("goal-creation" as never, updated as never);
    },
    [form],
  );

  const getCategoryDisplay = (categoryCode: string) => {
    if (!categoryCode) return undefined;
    return (
      GOAL_CATEGORIES.find((cat) => cat.code === categoryCode)?.display ??
      categoryCode
    );
  };

  return (
    <FormSection
      title="Treatment Goals"
      subtitle="Create treatment goals for the patient"
      showDivider={false}
    >
      <Stack gap="md">
        {goals.length === 0 && (
          <Text size="sm" c="dimmed">
            No goals added yet. Click "Add Goal" to create a treatment goal.
          </Text>
        )}

        {goals.map((goal, index) => {
          const goalNumber = index + 1;
          const categoryError =
            !goal.category && form.errors[`goal-creation.${index}.category`]
              ? (form.errors[`goal-creation.${index}.category`] as string)
              : undefined;
          const descriptionError =
            !goal.description &&
            form.errors[`goal-creation.${index}.description`]
              ? (form.errors[`goal-creation.${index}.description`] as string)
              : undefined;
          const hasError = !!(categoryError || descriptionError);

          return (
            <EncounterGoalCard
              key={index}
              title={`New Goal #${goalNumber}`}
              category={getCategoryDisplay(goal.category)}
              onRemove={() => removeGoal(index)}
              removeAriaLabel={`Remove new goal ${goalNumber}`}
              hasError={hasError}
              disabled={disabled}
            >
              <FormField label="Goal Category" required error={categoryError}>
                <Select
                  placeholder="Select a category"
                  data={categoryOptions}
                  value={goal.category || ""}
                  onChange={(value) =>
                    updateGoal(index, "category", value || "")
                  }
                  searchable
                  clearable={false}
                  disabled={disabled}
                  aria-label={`New goal ${goalNumber} category`}
                />
              </FormField>

              <FormField
                label="Goal Description"
                required
                error={descriptionError}
              >
                <Textarea
                  placeholder="Describe the goal..."
                  value={goal.description || ""}
                  onChange={(event) =>
                    updateGoal(index, "description", event.currentTarget.value)
                  }
                  minRows={2}
                  disabled={disabled}
                  aria-label={`New goal ${goalNumber} description`}
                />
              </FormField>

              <FormField label="Notes">
                <Textarea
                  placeholder="Add any additional notes about this goal..."
                  value={goal.notes || ""}
                  onChange={(event) =>
                    updateGoal(index, "notes", event.currentTarget.value)
                  }
                  minRows={2}
                  disabled={disabled}
                  aria-label={`New goal ${goalNumber} notes`}
                />
              </FormField>
            </EncounterGoalCard>
          );
        })}

        <Button
          leftSection={<IconPlus size={16} />}
          onClick={addGoal}
          disabled={disabled}
          variant="light"
          color="purple"
        >
          Add Goal
        </Button>
      </Stack>
    </FormSection>
  );
};
